/**
 * MONEY, EXACTLY. One place, so there is one answer.
 *
 * ## Why this file exists
 *
 * `BigInt("22,000")` throws `SyntaxError: Cannot convert 22,000 to a BigInt`, and the withdraw screen
 * did exactly that: it read the KES balance the API had already FORMATTED for display and fed the
 * string straight into a parser that only understands digits. The throw happened inside a `useMemo`
 * during render, so it escaped the component entirely and came out as the router's own
 * "Unexpected Application Error" page, with the participant's balance nowhere on screen.
 *
 * The bug is not the missing `replace(/,/g, '')`. It is that four screens had each written their own
 * private version of this conversion, so a formatted string was safe in one and fatal in the next.
 * They are consolidated here.
 *
 * ## The rules, which are the backend's rules
 *
 * - **A money amount is an integer in minor units.** `22000.00` KES is `2200000n`. Never a float:
 *   `Number('1234.56') * 100` is `123456.00000000001`, and a limit check that is wrong by a
 *   hundredth of a cent is a limit check that sometimes says the wrong thing for reasons nobody can
 *   reproduce. This mirrors `Money` in `lipa.contracts`, which has no `__mul__` with a float on
 *   purpose.
 * - **Display formatting is stripped before parsing, never after.** Group separators, spaces and the
 *   non-breaking spaces `Intl` emits all come off first.
 * - **Nothing is silently rounded.** More decimal places than the asset has is a REFUSAL, not a
 *   truncation. The backend's `parse_money` behaves the same way, and a UI that quietly dropped a
 *   digit would send a different number than the one the participant typed.
 * - **`decimals` is a parameter, never a table.** Asset scale lives in the backend. The last time it
 *   lived in two places it was wrong in one of them by a factor of ten thousand, so callers that
 *   know the asset pass its scale and callers that only handle KES take the default.
 */

/** A number we will not guess at. Carries a sentence that can be shown to a participant. */
export class MoneyFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyFormatError';
  }
}

/** KES, and every other two-place fiat. The default, because most of this app is KES. */
export const FIAT_DECIMALS = 2;

/**
 * Strip DISPLAY formatting from a typed or rendered amount.
 *
 * Removes group separators (`,` `'` `_`), every flavour of space `Intl` produces (including U+00A0
 * and U+202F, which is what `toLocaleString` actually emits in several locales), and any currency
 * code or symbol sitting beside the number. What it does NOT do is interpret: it never reorders
 * digits and never converts a comma into a decimal point, because "1,5" is ambiguous and guessing is
 * how somebody sends fifteen thousand instead of one and a half.
 */
export function normalizeAmountInput(raw: string): string {
  return (raw ?? '')
    .replace(/[\s\u00a0\u202f\u2009]/g, '')
    .replace(/[,'_]/g, '')
    .replace(/[^0-9.-]/g, '');
}

/**
 * What a text input may contain WHILE somebody is typing.
 *
 * Deliberately more permissive than `toBaseUnits`: a half-typed "12." is not an error, it is a person
 * mid-keystroke. Validation happens when they act, not while they type.
 */
export function sanitizeAmountTyping(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
}

/**
 * A display amount to minor units. Exact, and it refuses what it cannot represent exactly.
 *
 * Accepts what a screen shows ("22,000.00") as readily as what a field holds ("22000"), which is the
 * whole point: the two are the same amount and only one of them used to parse.
 *
 * @throws MoneyFormatError on anything that is not a non-negative decimal with at most `decimals`
 *         places. The message is written to be shown to a participant.
 */
export function toBaseUnits(input: string, decimals: number = FIAT_DECIMALS): bigint {
  const value = normalizeAmountInput(input ?? '');

  if (value === '' || value === '.') throw new MoneyFormatError('Enter an amount.');
  if (value.startsWith('-')) throw new MoneyFormatError('Enter an amount above zero.');
  if (!/^\d*(\.\d*)?$/.test(value) || !/\d/.test(value)) {
    throw new MoneyFormatError('Enter a valid amount, using digits and a decimal point.');
  }

  const [whole, frac = ''] = value.split('.');

  if (frac.length > decimals) {
    throw new MoneyFormatError(
      decimals === 0
        ? 'This amount cannot have decimal places.'
        : `This amount cannot have more than ${decimals} decimal place${decimals === 1 ? '' : 's'}.`,
    );
  }

  const scaled = frac.padEnd(decimals, '0');
  return BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(scaled || '0');
}

/**
 * Minor units to a plain decimal string: `2200000n` -> `"22000.00"`.
 *
 * NO group separators, because this is the round-trip form. It is what goes back into a field, into
 * a query string, or onto the wire. `formatMoneyAmount` is the one for reading.
 */
export function fromBaseUnits(minor: bigint | string, decimals: number = FIAT_DECIMALS): string {
  const value =
    typeof minor === 'bigint' ? minor : BigInt(normalizeAmountInput(String(minor)) || '0');
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const sign = negative ? '-' : '';

  if (decimals === 0) return `${sign}${abs.toString()}`;

  const unit = 10n ** BigInt(decimals);
  return `${sign}${(abs / unit).toString()}.${(abs % unit).toString().padStart(decimals, '0')}`;
}

/**
 * Minor units to a string a person reads: `2200000n` -> `"22,000.00"`.
 *
 * Grouping is done on the digit string with a regex rather than through `Number`, so an amount larger
 * than 2^53 groups correctly instead of turning into `1.2345678901234568e+21`. A daily cap in minor
 * units of a low-denomination currency reaches that range in ordinary use.
 */
export function formatMoneyAmount(
  minor: bigint | string,
  decimals: number = FIAT_DECIMALS,
): string {
  const plain = fromBaseUnits(minor, decimals);
  const negative = plain.startsWith('-');
  const [whole, frac] = (negative ? plain.slice(1) : plain).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
}

/**
 * ASSET SCALE, FOR DISPLAY ONLY, and only where the wire leaves us no choice.
 *
 * Mirrors `ASSET_SCALE` in `backend/src/lipa/contracts/money.py`. Duplicating it is a cost, and the
 * cost is named in CLAUDE.md: the last time asset scale lived in two places it was wrong in one of
 * them by a factor of ten thousand. It is here anyway because `POST /transact/swap/quote` serves
 * `you_receive`, `spread` and `fee` as MINOR UNITS with no display field -- unlike `/rates/quote`,
 * which serves `{amount, asset, display}` -- so a screen rendering a swap quote has no other source
 * for where the decimal point goes. Without it the confirm screen reads "You receive 1000000 USDT"
 * for one dollar.
 *
 * THE FIX IS ON THE BACKEND, not here: `Quote.to_json` should carry a formatted `display` beside each
 * figure, as the convert quote already does, and this table should then be deleted. Until it does:
 *
 *   - NEVER use this to build an amount that is SENT anywhere. Amounts go out as decimal strings and
 *     the server parses them at the scale it holds.
 *   - An asset missing from this table is one the backend does not know either (`scale_of` raises),
 *     so falling back to two places is a display artefact, never a value error.
 */
export const ASSET_DECIMALS: Readonly<Record<string, number>> = {
  KES: 2,
  BTC: 8,
  ETH: 18,
  USDT: 6,
  USDC: 6,
};

export function decimalsFor(asset: string): number {
  return ASSET_DECIMALS[asset] ?? FIAT_DECIMALS;
}

/**
 * Minor units to a readable amount of `asset`, with trailing zeros trimmed.
 *
 * "1.000000 USDT" is noise; "1 USDT" is the number. Trimming happens on the STRING, after exact
 * conversion, so nothing is rounded on the way.
 */
export function formatAssetAmount(minor: bigint | string, asset: string): string {
  const decimals = decimalsFor(asset);
  // Some backends (VASP's rates/quote engine) return figures as DECIMAL strings ("0.00",
  // "7.722007") rather than the minor-unit integer counts this function was written for. A string
  // carrying a decimal point is already a human amount: format it directly (string-based, so no
  // precision is lost) instead of routing it through the minor-unit path, where `BigInt("0.00")`
  // throws "Cannot convert 0.00 to a BigInt" and takes the whole screen down. Integer strings and
  // bigints keep the exact minor-unit path below.
  if (typeof minor === 'string' && minor.includes('.')) {
    const negative = minor.trim().startsWith('-');
    const clean = minor.replace(/[,\s]/g, '').replace(/^-/, '');
    const [wholeRaw = '0', fracRaw = ''] = clean.split('.');
    const frac = decimals === FIAT_DECIMALS ? (fracRaw + '00').slice(0, 2) : fracRaw.replace(/0+$/, '');
    const grouped = (wholeRaw || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${negative ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
  }
  const formatted = formatMoneyAmount(minor, decimals);
  if (!formatted.includes('.')) return formatted;
  const trimmed = formatted.replace(/0+$/, '').replace(/\.$/, '');
  // Fiat keeps its two places: "KES 1,200" where every other balance says "KES 1,200.00" reads as a
  // different kind of number.
  return decimals === FIAT_DECIMALS ? formatted : trimmed;
}

/**
 * Group an amount the API already formatted: `"225000.00"` -> `"225,000.00"`.
 *
 * The backend's `Money.format()` emits no group separators, so every screen rendering a balance
 * straight from the wire shows `KES 225000.00`. That was hidden for months because the FIXTURES
 * carried commas the real service never sends, so mock mode looked right and production would not
 * have. Correcting the fixtures made it visible; this makes it right in both.
 *
 * NO ARITHMETIC. It walks the digit string and inserts separators, so an amount with eighteen decimal
 * places (ETH) survives untouched and nothing is ever rounded on the way to a screen. Anything it
 * does not recognise as a number is returned exactly as received, because a balance we cannot format
 * must still be SHOWN, not swallowed.
 */
export function formatWireAmount(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return raw;
  const negative = raw.startsWith('-');
  const [whole, frac] = (negative ? raw.slice(1) : raw).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
}

/** The result of parsing without throwing. */
export type ParsedMoney =
  { ok: true; minor: bigint; normalized: string } | { ok: false; reason: string };

/**
 * Parse for a FORM: a result, not an exception.
 *
 * Screens validate on every keystroke, and a try/catch per keystroke is how a validation branch ends
 * up swallowing an error it did not mean to. `reason` is already written for a participant.
 */
export function parseMoneyAmount(input: string, decimals: number = FIAT_DECIMALS): ParsedMoney {
  try {
    const minor = toBaseUnits(input, decimals);
    return { ok: true, minor, normalized: fromBaseUnits(minor, decimals) };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof MoneyFormatError ? err.message : 'Enter a valid amount.',
    };
  }
}

/**
 * A Kenyan mobile number, normalised to the form the M-Pesa rail expects (`2547XXXXXXXX`).
 *
 * Accepts what people actually type: `0712 345 678`, `+254 712 345 678`, `254712345678`. Returns null
 * when it is not one, so the caller shows "Enter a valid M-Pesa phone number" rather than sending a
 * payout instruction to a number that cannot exist.
 *
 * Safaricom and Airtel prefixes are both 7XX and 1XX under the Kenyan plan, which is why the test is
 * on the shape and not on a list of operator prefixes we would then have to maintain.
 */
export function normalizeKenyanMsisdn(raw: string): string | null {
  const digits = (raw ?? '').replace(/[^\d+]/g, '').replace(/^\+/, '');

  const local = /^0(7\d{8}|1\d{8})$/.exec(digits);
  if (local) return `254${local[1]}`;

  const international = /^254(7\d{8}|1\d{8})$/.exec(digits);
  if (international) return international[0];

  const bare = /^(7\d{8}|1\d{8})$/.exec(digits);
  if (bare) return `254${bare[1]}`;

  return null;
}

/** `254712345678` -> `0712 345 678`. Display only. */
export function formatKenyanMsisdn(msisdn: string): string {
  const normalized = normalizeKenyanMsisdn(msisdn);
  if (!normalized) return msisdn;
  const national = `0${normalized.slice(3)}`;
  return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
}
